/**
 * TransparencyPanel - Container showing AI processing status.
 *
 * Combines:
 * - ThinkingIndicator for status messages
 * - ToolTimeline for active tool calls
 *
 * Provides real-time visibility into what the AI is doing.
 */

import { Bot } from 'lucide-react'
import { ThinkingIndicator } from './ThinkingIndicator'
import { ToolTimeline } from '../ToolDisplay/ToolTimeline'
import type { ToolCall } from '@/store/chatStore'

interface TransparencyPanelProps {
  status: string | null
  toolCalls: ToolCall[]
  isLoading: boolean
}

export function TransparencyPanel({
  status,
  toolCalls,
  isLoading,
}: TransparencyPanelProps) {
  // Don't render if nothing to show
  if (!isLoading && !status && toolCalls.length === 0) {
    return null
  }

  return (
    <div className="flex justify-start animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="flex items-start gap-2 max-w-[90%] sm:max-w-[80%]">
        {/* Assistant avatar */}
        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center">
          <Bot className="w-4 h-4 text-accent" />
        </div>

        <div className="space-y-3">
          {/* Thinking indicator */}
          {(isLoading || status) && (
            <ThinkingIndicator status={status} />
          )}

          {/* Active tool calls */}
          {toolCalls.length > 0 && (
            <div className="bg-surface/80 backdrop-blur border border-surface-border rounded-2xl px-4 py-3">
              <ToolTimeline toolCalls={toolCalls} isStreaming />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TransparencyPanel
